// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { PaginationState, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVendorUsageQuery, type VendorUsageRow } from "@/api/hooks/use-vendors";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { ChainLink } from "@/components/shared/chain-link";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { ImportSourceBadge } from "@/components/shared/import-source-badge";
import { ImportStatusBadge } from "@/components/shared/import-status-badge";
import { ImportTypeBadge } from "@/components/shared/import-type-badge";
import { RowId } from "@/components/shared/row-id";
import { StoreLink } from "@/components/shared/store-link";
import { TimezoneFooterNote } from "@/components/shared/timezone-footer-note";
import { Badge } from "@/components/ui/badge";
import { getDefaultPageSize } from "@/lib/preferences";

type VendorUsageSortBy = "createdAt" | "id" | "type" | "status" | "usageType";

const USAGE_TYPE_STYLES: Record<string, string> = {
  ingress: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
  export:
    "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-900",
};

function UsageTypeBadge({ usageType }: { usageType: string }) {
  const { t } = useTranslation("back-office");
  return (
    <Badge
      variant="outline"
      className={`font-normal text-xs ${USAGE_TYPE_STYLES[usageType] ?? USAGE_TYPE_STYLES.ingress}`}
    >
      {t(`vendors.usage.kind.${usageType}`, usageType)}
    </Badge>
  );
}

interface VendorUsageTabProps {
  vendorName: string;
}

export function VendorUsageTab({ vendorName }: VendorUsageTabProps) {
  const { t } = useTranslation("back-office");

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: getDefaultPageSize() });
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);

  const sortBy = (sorting[0]?.id ?? "createdAt") as VendorUsageSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const { data, isLoading, refetch, isFetching } = useVendorUsageQuery({
    vendorName,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    sortBy,
    orderBy,
  });

  const storeMap = useMemo(() => {
    const map = new Map<number, { storeName: string; legibleName: string }>();
    for (const s of data?.stores ?? []) map.set(s.storeId, { storeName: s.storeName, legibleName: s.legibleName });
    return map;
  }, [data?.stores]);

  const columns = useMemo<DataTableColumnDef<VendorUsageRow>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("vendors.usage.columns.id"),
        size: 90,
        meta: { columnLabel: t("vendors.usage.columns.id"), className: "pl-4", forceWidth: true },
        cell: ({ row }) =>
          row.original.id != null ? <RowId id={row.original.id} /> : <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: "usageType",
        header: t("vendors.usage.columns.usage"),
        size: 110,
        meta: { columnLabel: t("vendors.usage.columns.usage"), forceWidth: true },
        cell: ({ row }) => <UsageTypeBadge usageType={row.original.usageType} />,
      },
      {
        accessorKey: "type",
        header: t("vendors.usage.columns.type"),
        meta: { columnLabel: t("vendors.usage.columns.type") },
        cell: ({ row }) =>
          row.original.type ? (
            <ImportTypeBadge type={row.original.type} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "storeCode",
        header: t("vendors.usage.columns.store"),
        enableSorting: false,
        meta: { columnLabel: t("vendors.usage.columns.store") },
        cell: ({ row }) => {
          const { storeId, storeCode } = row.original;
          const store = storeId != null ? storeMap.get(storeId) : undefined;
          if (store) {
            return (
              <StoreLink storeName={store.storeName} legibleName={store.legibleName || storeCode || store.storeName} />
            );
          }
          // No entry in the relevant-stores response means the store was deleted — show whatever
          // identifier we have (store code preferred, else the id) marked as deleted.
          const identifier = storeCode ?? (storeId != null ? String(storeId) : null);
          if (identifier != null) {
            return <span className="text-muted-foreground">{t("vendors.usage.storeDeleted", { id: identifier })}</span>;
          }
          return <span className="text-muted-foreground">—</span>;
        },
      },
      {
        accessorKey: "chainName",
        header: t("vendors.usage.columns.chain"),
        enableSorting: false,
        meta: { columnLabel: t("vendors.usage.columns.chain"), defaultHidden: true },
        cell: ({ row }) =>
          row.original.chainName ? (
            <ChainLink chainName={row.original.chainName} legibleName={row.original.chainName} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "source",
        header: t("vendors.usage.columns.source"),
        enableSorting: false,
        meta: { columnLabel: t("vendors.usage.columns.source"), defaultHidden: true },
        cell: ({ row }) =>
          row.original.source ? (
            <ImportSourceBadge source={row.original.source} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "createdAt",
        header: t("vendors.usage.columns.createdAt"),
        meta: { columnLabel: t("vendors.usage.columns.createdAt") },
        cell: ({ row }) => <DateTimeValue value={row.original.createdAt} />,
      },
      {
        accessorKey: "status",
        header: t("vendors.usage.columns.status"),
        size: 110,
        meta: { columnLabel: t("vendors.usage.columns.status") },
        cell: ({ row }) => <ImportStatusBadge status={row.original.status} />,
      },
    ],
    [t, storeMap],
  );

  return (
    <div className="p-6">
      <DataTable
        storageKey="vendor-usage"
        columns={columns}
        data={data?.usage ?? []}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        onRefresh={refetch}
        isRefreshing={isFetching}
        footerNote={<TimezoneFooterNote />}
      />
    </div>
  );
}
